/**
 * 报销单（事后，延伸需求 3.2）
 *
 * 【流程】先建单填明细 → 再上传发票（上传即触发 OCR/验真/查重）→ 提交审批 → 审批 → 出纳付款 → 归档。
 *
 * 【为什么建单与上传发票分开】上传发票会同步跑完外部校验（OCR → 企查查 → 验真），
 * 那是几十秒的活。混在一个「新建」动作里会让人以为页面卡住了；
 * 分开之后每一步都有明确的前置（单子已存在），失败也只需重做那一步。
 */

import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Tag,
} from 'antd';
import { DeleteOutlined, PlusOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  ExpenseReimburseStatus,
  EXPENSE_REIMBURSE_STATUS_LABELS,
  ExpenseType,
  EXPENSE_TYPE_LABELS,
  PermissionCode,
  RiskLevel,
} from '@ilc/shared';
import type { ExpenseReimburse } from '@ilc/shared';
import { expenseApi, projectApi, contractApi } from '@/api/endpoints';
import { usePagedList } from '@/hooks/usePagedList';
import { useResource } from '@/hooks/useResource';
import { useAuthStore } from '@/stores/auth';
import { DataTable } from '@/components/DataTable';
import { PageHeader } from '@/components/PageHeader';
import { Money, moneyCellStyle } from '@/components/Money';
import { RiskTag } from '@/components/RiskTag';
import { MaskedField } from '@/components/MaskedField';
import { feedback } from '@/utils/feedback';
import { performAction } from '@/utils/performAction';

interface Query {
  page?: number;
  pageSize?: number;
  keyword?: string;
  expenseType?: ExpenseType;
  status?: ExpenseReimburseStatus;
  riskLevel?: RiskLevel;
  mine?: boolean;
}

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'default',
  APPROVING: 'processing',
  APPROVED: 'success',
  REJECTED: 'warning',
  PAID: 'success',
  ARCHIVED: 'default',
  CANCELLED: 'default',
};

/** 明细行在表单里的形状：金额用「元」，提交时转分 */
interface ItemForm {
  itemDate: dayjs.Dayjs;
  subject: string;
  amountYuan: number;
  expenseType?: ExpenseType;
  category?: string | null;
  remark?: string | null;
}

export function ExpenseReimbursePage(): React.ReactElement {
  const can = useAuthStore((s) => s.can);
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const canReimburse = can(PermissionCode.EXPENSE_REIMBURSE);

  const list = usePagedList<ExpenseReimburse, Query>({
    loader: (q) => expenseApi.listReimburse(q),
    initialQuery: { page: 1, pageSize: 20 },
  });

  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [budgetWarning, setBudgetWarning] = useState<string | null>(null);
  const [form] = Form.useForm();

  const projects = useResource(() => projectApi.list(), { deps: [] });
  /* 合同下拉走**候选接口**（只回 id/合同号/名称，按数据范围过滤）。
   * 调列表接口要 contract:read，而普通员工与部门负责人没有这个码——
   * 页面一打开就会弹「没有执行该操作的权限（需要：contract:read）」。 */
  const contracts = useResource(() => contractApi.options(), { deps: [] });

  /** 从「费用申请」页跳过来时预选申请单；预填只在打开时发生，不覆盖用户输入 */
  const preApplyId = search.get('applyId');
  const formInitialValues = useMemo(
    () => ({
      expenseType: ExpenseType.TRAVEL,
      applyId: preApplyId ?? undefined,
      // 金额留空（undefined）而不是 0：0 元是一条合法但没有意义的明细，
      // 预填 0 会让人以为"默认零元"，直接提交就成了一张 0 元报销单
      items: [{ itemDate: dayjs(), subject: '', amountYuan: undefined }] as unknown as ItemForm[],
    }),
    [preApplyId],
  );

  const columns = [
    {
      title: '报销单号',
      dataIndex: 'reimburseNo',
      width: 170,
      render: (v: string, r: ExpenseReimburse) => (
        <Link to={`/business/expenses/reimburse/${r.id}`} className="ilc-mono">
          {v}
        </Link>
      ),
    },
    { title: '申请人', dataIndex: 'applicantName', width: 100, render: (v: string | null) => v ?? '-' },
    {
      title: '费用类型',
      dataIndex: 'expenseType',
      width: 100,
      render: (v: string) => EXPENSE_TYPE_LABELS[v as ExpenseType] ?? v,
    },
    {
      title: '报销金额',
      dataIndex: 'amountCents',
      width: 130,
      align: 'right' as const,
      onCell: () => ({ style: moneyCellStyle }),
      // 金额由明细汇总得出，未填明细时为 0——这里如实显示 0 而不是「-」，
      // 因为 0 元的报销单确实存在（还没填明细）
      render: (v: number) => <Money cents={v} strong />,
    },
    { title: '明细', dataIndex: 'itemCount', width: 70, render: (v: number) => v ?? 0 },
    {
      title: '发票',
      dataIndex: 'invoiceCount',
      width: 70,
      render: (v: number) =>
        (v ?? 0) > 0 ? <Tag color="blue">{v}</Tag> : <span style={{ color: 'rgba(0,0,0,0.35)' }}>-</span>,
    },
    {
      title: '风险',
      dataIndex: 'riskLevel',
      width: 90,
      render: (v: string) => (v && v !== RiskLevel.NONE ? <RiskTag level={v as RiskLevel} /> : <Tag>无</Tag>),
    },
    { title: '报销事由', dataIndex: 'reason', ellipsis: true },
    {
      title: '收款账户',
      dataIndex: 'payeeAccount',
      width: 170,
      // 银行账号是敏感字段（需求 4 字段权限），按权限脱敏
      render: (v: string | null) => (
        <MaskedField value={v} kind="bankAccount" permission={PermissionCode.EXPENSE_PAY} />
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 96,
      render: (v: string) => (
        <Tag color={STATUS_COLORS[v]}>
          {EXPENSE_REIMBURSE_STATUS_LABELS[v as ExpenseReimburseStatus] ?? v}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 140,
      fixed: 'right' as const,
      render: (_: unknown, r: ExpenseReimburse) => (
        <Space size={4}>
          <Button size="small" type="link" onClick={() => navigate(`/business/expenses/reimburse/${r.id}`)}>
            {r.status === ExpenseReimburseStatus.DRAFT ? '继续填写' : '查看'}
          </Button>
          {canReimburse && r.status === ExpenseReimburseStatus.DRAFT && (
            <Button
              size="small"
              type="link"
              onClick={() => navigate(`/business/expenses/reimburse/${r.id}?submit=1`)}
            >
              提交
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const save = async (): Promise<void> => {
    const v = await form.validateFields();
    const items: ItemForm[] = v.items ?? [];
    if (items.length === 0) {
      feedback.warning('至少要有一条费用明细');
      return;
    }
    setSaving(true);
    try {
      const result = await performAction({
        action: () =>
          expenseApi.createReimburse({
            expenseType: v.expenseType,
            reason: v.reason,
            applyId: v.applyId ?? null,
            projectId: v.projectId ?? null,
            contractId: v.contractId ?? null,
            budgetId: null,
            payeeName: v.payeeName || null,
            payeeAccount: v.payeeAccount || null,
            payeeBank: v.payeeBank || null,
            items: items.map((it, i) => ({
              itemDate: dayjs(it.itemDate).format('YYYY-MM-DD'),
              subject: it.subject,
              amountCents: Math.round(Number(it.amountYuan ?? 0) * 100),
              expenseType: it.expenseType,
              category: it.category ?? null,
              remark: it.remark ?? null,
              sortOrder: i,
            })),
          }),
        successMessage: '报销单已保存',
        offline: () => undefined,
        offlineMessage: '后端费用接口尚未就绪，本次保存仅在本地演示',
      });
      if (!result.ok) return;
      setCreating(false);
      form.resetFields();
      list.reload();
      const id = (result.data as { id?: string } | null)?.id;
      if (id) {
        // 建单成功直接进详情页上传发票——那是必须做的下一步，别让人自己找
        feedback.info('请继续上传发票：上传时会自动完成 OCR、验真与重复报销查重');
        navigate(`/business/expenses/reimburse/${id}`);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ilc-page">
      <PageHeader
        title="报销单"
        description="事后报销：填明细、传发票、走审批、出纳付款"
        extra={
          <Space>
            {canReimburse && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
                新建报销单
              </Button>
            )}
            <Button icon={<ReloadOutlined />} onClick={list.reload}>
              刷新
            </Button>
          </Space>
        }
      />

      {budgetWarning && (
        <Alert
          type="warning"
          showIcon
          closable
          style={{ marginBottom: 12 }}
          message="预算提示"
          description={budgetWarning}
          onClose={() => setBudgetWarning(null)}
        />
      )}

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="同一张发票不能重复报销——数据库层就挡住了，不是靠界面提示"
        description={
          <span>
            上传发票时会自动做四项检查：是否本公司抬头、报销金额与发票金额是否一致、
            开票品类与费用类型是否匹配、这张票是否已被别的报销单占用。
            报销单的风险等级会**并回发票自身的风险**（红冲、作废等），不会只看这四项。
          </span>
        }
      />

      <DataTable<ExpenseReimburse>
        storageKey="expense-reimburse"
        exportFileName="报销单"
        exportColumns={[
          { title: '报销单号', dataIndex: 'reimburseNo' },
          { title: '申请人', dataIndex: 'applicantName' },
          { title: '费用类型', value: (r) => EXPENSE_TYPE_LABELS[r.expenseType] ?? r.expenseType },
          { title: '报销金额(元)', value: (r) => (r.amountCents / 100).toFixed(2) },
          { title: '明细条数', dataIndex: 'itemCount' },
          { title: '发票张数', dataIndex: 'invoiceCount' },
          { title: '风险等级', dataIndex: 'riskLevel' },
          { title: '报销事由', dataIndex: 'reason' },
          { title: '状态', value: (r) => EXPENSE_REIMBURSE_STATUS_LABELS[r.status] ?? r.status },
          { title: '退回次数', dataIndex: 'rejectCount' },
          { title: '最近退回原因', dataIndex: 'lastRejectReason' },
        ]}
        columns={columns}
        dataSource={list.list}
        rowKey="id"
        loading={list.loading}
        error={list.error}
        isFallback={list.isFallback}
        fallbackReason={list.fallbackReason}
        onRetry={list.reload}
        pagination={list.pagination}
        onChange={list.handleTableChange}
        scrollX={1500}
        emptyText="还没有报销单。点右上角「新建报销单」开始。"
        toolbar={
          <Space wrap>
            <Input.Search
              allowClear
              placeholder="单号 / 事由"
              style={{ width: 220 }}
              onSearch={(v) => list.setQuery({ keyword: v || undefined })}
            />
            <Select
              allowClear
              placeholder="费用类型"
              style={{ width: 130 }}
              value={list.query.expenseType}
              onChange={(v) => list.setQuery({ expenseType: v })}
              options={Object.entries(EXPENSE_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
            />
            <Select
              allowClear
              placeholder="状态"
              style={{ width: 120 }}
              value={list.query.status}
              onChange={(v) => list.setQuery({ status: v })}
              options={Object.entries(EXPENSE_REIMBURSE_STATUS_LABELS).map(([value, label]) => ({
                value,
                label,
              }))}
            />
            <Select
              allowClear
              placeholder="风险等级"
              style={{ width: 120 }}
              value={list.query.riskLevel}
              onChange={(v) => list.setQuery({ riskLevel: v })}
              options={[
                { value: RiskLevel.HIGH, label: '高' },
                { value: RiskLevel.MEDIUM, label: '中' },
                { value: RiskLevel.LOW, label: '低' },
                { value: RiskLevel.NONE, label: '无' },
              ]}
            />
            <Button
              type={list.query.mine === true ? 'primary' : 'default'}
              onClick={() => list.setQuery({ mine: list.query.mine === true ? undefined : true })}
            >
              只看我的
            </Button>
            <Button onClick={list.resetQuery}>重置</Button>
          </Space>
        }
      />

      <Modal
        open={creating}
        title="新建报销单"
        width={900}
        onCancel={() => setCreating(false)}
        onOk={() => void save()}
        confirmLoading={saving}
        destroyOnHidden
        okText="保存并去上传发票"
      >
        <Form key="create" form={form} layout="vertical" preserve={false} initialValues={formInitialValues}>
          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item name="expenseType" label="费用类型" rules={[{ required: true }]}>
                <Select
                  options={Object.entries(EXPENSE_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={16}>
              <Form.Item name="reason" label="报销事由" rules={[{ required: true, message: '请填写事由' }]}>
                <Input placeholder="如：泰安出差交通与住宿费" maxLength={1000} />
              </Form.Item>
            </Col>
          </Row>

          {/* —— 费用明细 —— */}
          <Form.Item
            label="费用明细"
            required
            extra="报销金额 = 明细金额之和（系统不单独存一个金额字段，避免两处对不上）"
          >
            <Form.List name="items">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field) => (
                    <Row key={field.key} gutter={8} style={{ marginBottom: 4 }}>
                      <Col span={5}>
                        <Form.Item
                          name={[field.name, 'itemDate']}
                          rules={[{ required: true, message: '日期' }]}
                          style={{ marginBottom: 4 }}
                        >
                          <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col span={7}>
                        <Form.Item
                          name={[field.name, 'subject']}
                          rules={[{ required: true, message: '请填事项' }]}
                          style={{ marginBottom: 4 }}
                        >
                          <Input placeholder="事项" />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item
                          name={[field.name, 'amountYuan']}
                          rules={[{ required: true, message: '金额' }]}
                          style={{ marginBottom: 4 }}
                        >
                          <InputNumber style={{ width: '100%' }} precision={2} min={0} placeholder="元" />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item name={[field.name, 'category']} style={{ marginBottom: 4 }}>
                          <Input placeholder="品类（选填）" />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          disabled={fields.length <= 1}
                          onClick={() => remove(field.name)}
                        >
                          删除
                        </Button>
                      </Col>
                    </Row>
                  ))}
                  <Button type="dashed" onClick={() => add({ itemDate: dayjs() })} icon={<PlusOutlined />} block>
                    添加明细行
                  </Button>
                </>
              )}
            </Form.List>
          </Form.Item>

          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item name="projectId" label="关联工程" extra="可选">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="可选"
                  loading={projects.loading}
                  notFoundContent={projects.loading ? '加载中…' : '暂无工程'}
                  options={(projects.data ?? []).map((p: { id: string; name: string }) => ({
                    value: p.id,
                    label: p.name,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="contractId" label="关联合同" extra="关联后发票会计入该合同的开票额度">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="可选"
                  loading={contracts.loading}
                  notFoundContent={contracts.loading ? '加载中…' : '暂无合同'}
                  options={(contracts.data ?? []).map((c) => ({
                    value: c.id,
                    label: `${c.contractNo} ${c.name}`,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="applyId"
                label="关联事前申请"
                extra={
                  preApplyId
                    ? '已从「费用申请」页带入'
                    : '关联后本单金额会占用该申请的可报销余额'
                }
              >
                <Input className="ilc-mono" placeholder="从费用申请页点「去报销」自动带入" readOnly />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item name="payeeName" label="收款人">
                <Input maxLength={200} placeholder="默认本人" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="payeeAccount" label="收款账号" extra="敏感字段，按权限脱敏展示">
                <Input className="ilc-mono" maxLength={64} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="payeeBank" label="开户行">
                <Input maxLength={200} />
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          message="保存后请继续上传发票"
          description="上传即触发 OCR 识别、发票验真、供应商校验与重复报销查重。没有发票的费用明细会在校验中提示「未关联发票」。"
        />
      </Modal>
    </div>
  );
}

export default ExpenseReimbursePage;
